import { useEffect, useRef } from "react";

const MAX_GAP_MS = 55;
const MIN_LENGTH = 4;

/** Captures USB/Bluetooth HID scanners, which emit a rapid key sequence
 * followed by Enter. Editable controls are ignored so normal typing and
 * accessibility are never hijacked. Camera scanning is a separate input
 * transport and can feed the same onScan callback later. */
export function useBarcodeScanner(onScan: (barcode: string) => void, enabled = true): void {
  const callbackRef = useRef(onScan);
  const bufferRef = useRef("");
  const lastKeyAtRef = useRef(0);
  const startedAtRef = useRef(0);
  callbackRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    const reset = () => {
      bufferRef.current = "";
      lastKeyAtRef.current = 0;
      startedAtRef.current = 0;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey || event.isComposing) return;
      const now = performance.now();
      if (event.key === "Enter" || event.key === "Tab") {
        const code = bufferRef.current.trim();
        const scannerSpeed = lastKeyAtRef.current > 0 && now - lastKeyAtRef.current <= MAX_GAP_MS && now - startedAtRef.current <= code.length * MAX_GAP_MS;
        reset();
        if (code.length >= MIN_LENGTH && scannerSpeed) {
          event.preventDefault();
          // A scanner often fires while the search box still owns focus.
          // Remove only the just-scanned suffix; preserve anything the user
          // had typed before it, then notify React through a real input event.
          const target = event.target;
          if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
            if (target.value.endsWith(code)) {
              target.value = target.value.slice(0, -code.length);
              target.dispatchEvent(new Event("input", { bubbles: true }));
            }
          }
          callbackRef.current(code);
        }
        return;
      }
      if (event.key.length !== 1) {
        if (event.key === "Escape") reset();
        return;
      }
      if (lastKeyAtRef.current && now - lastKeyAtRef.current > MAX_GAP_MS) {
        bufferRef.current = "";
        startedAtRef.current = now;
      }
      if (!bufferRef.current) startedAtRef.current = now;
      bufferRef.current += event.key;
      lastKeyAtRef.current = now;
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled]);
}
